import { AXIS_EVIDENCE_MAP, currentAxisWeight } from "@shared/labs/axis-weights";
import type {
  AxisScore,
  CorrectionCallback,
  Evidence,
  ThreeAxisRecommendation,
} from "@shared/labs/three-axis";
import { useState } from "react";

export interface AxesSectionProps {
  rec: ThreeAxisRecommendation;
  onCorrection?: CorrectionCallback;
}

const DIM_DISPLAY_NAME: Record<string, string> = {
  academic: "Acadêmico",
  // Kept for legacy assessments rendered post-cutover; new evals use trajectory.
  company_era: "Histórico",
  growth_within: "Crescimento",
  trajectory: "Trajetória",
  tenure: "Estabilidade",
  founder: "Fundador",
};

/** Generate the "<n> evidências entram em <axis>..." preamble straight from
 *  the live AXIS_EVIDENCE_MAP so a rebalance never leaves stale copy
 *  embedded in the page. Lists weights in descending order with neutral
 *  grammar so it reads cleanly even when dims have mixed genders or ties. */
function buildAxisPlainCopy(axisId: "gca" | "compWay"): string {
  const entries = [...AXIS_EVIDENCE_MAP[axisId]].sort((a, b) => b.weight - a.weight);
  if (entries.length === 0) return "";
  const list = entries
    .map((e) => `${DIM_DISPLAY_NAME[e.dim] ?? e.dim} ${e.weight.toFixed(2)}`)
    .join(" · ");
  return `${entries.length} evidências · pesos: ${list}.`;
}

const AX_PLAIN: Record<"gca" | "compWay", string> = {
  gca: buildAxisPlainCopy("gca"),
  compWay: buildAxisPlainCopy("compWay"),
};

const OTHER_AXIS_LABEL: Record<"gca" | "compWay", string> = {
  gca: "Comp Way",
  compWay: "GCA",
};

const TOTAL_LABEL: Record<"gca" | "compWay", string> = {
  gca: "Nota GCA",
  compWay: "Nota Comp Way",
};

function miniStars(score: number) {
  // Numeric chrome only — design v8 drops the star glyphs. The stars-val span
  // next to this still renders "N / 5" tabular-nums, which carries the value.
  const full = Math.floor(score);
  const hasHalf = score - full >= 0.25 && score - full < 0.75;
  return (
    <>
      {([0, 1, 2, 3, 4] as const).map((i) => {
        const cls = i < full ? "mini-s on" : i === full && hasHalf ? "mini-s half" : "mini-s";
        return <span key={`ms-${i}`} className={cls} aria-hidden />;
      })}
    </>
  );
}

function marginBadge(axis: AxisScore): { cls: string; label: string } {
  if (axis.pass && axis.closeCall) {
    return { cls: "margin-badge close", label: `margem +${axis.margin.toFixed(2)} (borderline)` };
  }
  if (axis.pass) {
    return { cls: "margin-badge", label: `margem +${axis.margin.toFixed(2)}` };
  }
  return { cls: "margin-badge fail", label: `${axis.margin.toFixed(2)} abaixo` };
}

interface AxisPanelProps {
  axis: AxisScore & { id: "gca" | "compWay" };
  evidences: Evidence[];
  sensitivity: ThreeAxisRecommendation["sensitivity"];
}

function AxisPanel({ axis, evidences, sensitivity }: AxisPanelProps) {
  const axisId = axis.id;
  // CHRO adjustment on aggregated GCA / Comp Way is disabled — users correct
  // only at the evidence or skill level, and those roll up into the axes.
  const corrected = axis.chroCorrection;

  // Collect evidences that contribute to this axis, sorted by weight desc
  // Stored evaluations carry frozen `contributions[].weight` snapshots that
  // can drift from the current AXIS_EVIDENCE_MAP after a rebalance. The
  // aggregator's math already runs on current weights — render the table
  // against the same source so what the operator sees matches what was
  // computed. Falls back to the stored weight only when the dim isn't in
  // the live map (defensive; shouldn't happen in normal use).
  const isWeightedAxis = axisId !== "skills";
  const rows: Array<{
    ev: Evidence;
    score: number;
    weight: number;
    contribution: number;
    otherAxisLabel?: string;
    otherWeight?: number;
  }> = evidences
    .flatMap((ev) => {
      const c = ev.contributions.find((ct) => ct.axis === axisId);
      if (!c) return [];
      const score = ev.score ?? 0;
      const liveWeight = isWeightedAxis ? currentAxisWeight(axisId, ev.id) || c.weight : c.weight;
      const contribution = (score / 5) * liveWeight;
      const other = ev.contributions.find((ct) => ct.axis !== axisId && ct.axis !== "skills");
      const otherLiveWeight = other
        ? other.axis === "skills"
          ? other.weight
          : currentAxisWeight(other.axis, ev.id) || other.weight
        : undefined;
      return [
        {
          ev,
          score,
          weight: liveWeight,
          contribution,
          otherAxisLabel: other ? OTHER_AXIS_LABEL[axisId] : undefined,
          otherWeight: otherLiveWeight,
        },
      ];
    })
    .sort((a, b) => b.weight - a.weight);

  // Build cumulative starts
  let cum = 0;
  const rowsWithCum = rows.map((r) => {
    const start = cum;
    cum += r.contribution;
    return { ...r, cumulativeStart: start };
  });

  const { cls: mbCls, label: mbLabel } = marginBadge(axis);

  const sensEntry = sensitivity.find((s) => s.axis === axisId);

  let teaser = "";
  if (sensEntry) {
    if (sensEntry.fragile && axis.pass) {
      teaser = `${sensEntry.fragilityRatio} perturbações reprovam — decisão frágil`;
    } else if (!axis.pass) {
      teaser = "gap estrutural — nenhum ajuste isolado flipa";
    } else {
      teaser = "decisão robusta a perturbações";
    }
  }

  return (
    <div className={`axis-panel${corrected ? " has-adjust" : ""}`} id={`axis-${axisId}`}>
      <div className="ax-meta-row">
        <span className={`ax-state ${axis.pass ? "pass" : "fail"}`}>
          {axis.pass ? "passou" : "reprovado"} · corte {axis.threshold.toFixed(2)}
        </span>
        {corrected && (
          <span className="adj-badge">
            {`ajustada · ${corrected.originalScore.toFixed(2)} → ${corrected.correctedScore.toFixed(2)}`}
          </span>
        )}
      </div>
      <p className="ax-plain">{AX_PLAIN[axisId]}</p>

      <table className="contrib">
        <thead>
          <tr>
            <th>Evidência</th>
            <th className="right">Nota</th>
            <th className="right">Peso</th>
            <th className="right">Contribuição</th>
            <th className="bar-head" />
          </tr>
        </thead>
        <tbody>
          {rowsWithCum.map(
            ({ ev, score, weight, contribution, cumulativeStart, otherAxisLabel, otherWeight }) => (
              <tr key={ev.id}>
                <td>
                  <div className="ev-label">{ev.name}</div>
                  <div className="mini-stars">
                    {miniStars(score)}
                    <span className="stars-val">{score.toFixed(1)} / 5</span>
                  </div>
                  {otherAxisLabel !== undefined && otherWeight !== undefined && (
                    <div className="soft-split">
                      também pontua {otherAxisLabel} · peso {otherWeight.toFixed(2)}
                    </div>
                  )}
                </td>
                <td className="right weight">{Math.round((score / 5) * 100)}%</td>
                <td className="right weight">× {weight.toFixed(2)}</td>
                <td className="right">
                  <span className="contrib-val">{contribution.toFixed(3)}</span>
                </td>
                <td className="bar-cell">
                  <div className="contrib-bar">
                    <div
                      className={`contrib-bar-fill ${!axis.pass ? "fail" : ""}`}
                      style={{
                        left: `${cumulativeStart * 100}%`,
                        width: `${contribution * 100}%`,
                      }}
                    />
                  </div>
                </td>
              </tr>
            ),
          )}
          <tr className="total">
            <td className="label-cell" colSpan={3}>
              {TOTAL_LABEL[axisId]}
            </td>
            <td className="right">
              <span className={`total-val ${!axis.pass ? "fail" : ""}`}>
                {axis.value.toFixed(2)}
              </span>
            </td>
            <td className="bar-cell">
              <div className="contrib-bar">
                <div
                  className={`contrib-bar-fill ${!axis.pass ? "fail" : ""}`}
                  style={{ left: "0%", width: `${axis.value * 100}%` }}
                />
                <div
                  className="contrib-thresh"
                  style={{ left: `${axis.threshold * 100}%` }}
                  data-label={`corte ${axis.threshold.toFixed(2)}`}
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className={`threshold-strip ${!axis.pass ? "fail" : ""}`}>
        <span className="t-label">{`Corte para aprovar: ${axis.threshold.toFixed(2)}`}</span>
        <span className={mbCls}>{mbLabel}</span>
      </div>

      {sensEntry && (
        <details className="sens" id={`sens-${axisId}`}>
          <summary className="sens-summary">
            <span className="sens-head">sensibilidade</span>
            <span className="sens-teaser">{teaser}</span>
            <span className="sens-chev" aria-hidden>
              <svg viewBox="0 0 14 14" width="12" height="12" focusable="false" role="presentation">
                <title>chevron</title>
                <path
                  d="M3 5.5 L7 9.5 L11 5.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </summary>
          <div className="sens-body">
            <p className="sens-lede">
              {axis.pass
                ? "A decisão aguenta pequenos erros de calibração?"
                : "O que seria preciso para mudar a decisão?"}
            </p>
            <div className="sens-grid">
              {sensEntry.perturbations.map((p) => {
                const passesAfter = p.resultingScore >= axis.threshold;
                return (
                  <div
                    key={`${p.evidenceId}-${p.delta}`}
                    className="s-row"
                    style={{ display: "contents" }}
                  >
                    <div className="s-var">{p.evidenceId}</div>
                    <div className={`s-score ${p.flipsVerdict || !passesAfter ? "fail" : ""}`}>
                      {p.resultingScore.toFixed(2)}
                    </div>
                    <div className={`s-verdict ${!passesAfter ? "fail" : ""}`}>
                      {passesAfter ? "passa" : "reprova"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </details>
      )}
    </div>
  );
}

const TAB_TITLES: Record<"gca" | "compWay", string> = {
  gca: "GCA · contribuição das evidências",
  compWay: "Comp Way · contribuição das evidências",
};

export function AxesSection({ rec }: AxesSectionProps) {
  const nonSkillsAxes = rec.axes.filter(
    (a): a is AxisScore & { id: "gca" | "compWay" } => a.id === "gca" || a.id === "compWay",
  );

  const [activeTab, setActiveTab] = useState<"gca" | "compWay">(
    (nonSkillsAxes[0]?.id ?? "gca") as "gca" | "compWay",
  );
  const activeAxis = nonSkillsAxes.find((a) => a.id === activeTab) ?? nonSkillsAxes[0];

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-head-left">
          <h3>{activeAxis ? TAB_TITLES[activeAxis.id] : "Cálculo das notas"}</h3>
          <div className="sub">
            Como cada evidência decidiu a nota final do eixo. O traço vertical marca o corte de
            aprovação.
          </div>
        </div>
        <div className="seg" role="tablist" aria-label="Eixo">
          {nonSkillsAxes.map((a) => (
            <button
              key={a.id}
              type="button"
              role="tab"
              aria-selected={activeTab === a.id}
              className={activeTab === a.id ? "active" : ""}
              onClick={() => setActiveTab(a.id)}
              data-testid={`axes-tab-${a.id}`}
            >
              {a.id === "gca" ? "GCA" : "Comp Way"}
            </button>
          ))}
        </div>
      </div>
      <div className="card-body">
        {activeAxis && (
          <AxisPanel
            key={activeAxis.id}
            axis={activeAxis}
            evidences={rec.evidences}
            sensitivity={rec.sensitivity}
          />
        )}
      </div>
    </div>
  );
}
